import React, { useEffect, useState } from 'react';

// Fecha y hora de la gala. Es configuración interna de la cuenta atrás:
// en ningún sitio de la web se muestra esta hora, solo el tiempo restante.
export const GALA_DATETIME = '2026-12-10T18:00:00+01:00';

interface CountdownProps {
  /** Fecha/hora objetivo en ISO 8601, con zona horaria explícita. */
  target: string;
  /** Texto opcional que se muestra una vez alcanzada la fecha objetivo. */
  doneLabel?: string;
}

interface Remaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  done: boolean;
}

const getRemaining = (targetMs: number): Remaining => {
  const diff = targetMs - Date.now();
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, done: true };
  }
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds, done: false };
};

const UNITS: [keyof Remaining, string][] = [
  ['days', 'Días'],
  ['hours', 'Horas'],
  ['minutes', 'Min'],
  ['seconds', 'Seg'],
];

/**
 * Cuenta atrás genérica. La hora exacta del objetivo (`target`) es solo
 * configuración interna: en pantalla nunca se muestra la hora, solo los
 * días/horas/minutos/segundos que faltan.
 */
const Countdown: React.FC<CountdownProps> = ({ target, doneLabel = '¡Ya está aquí!' }) => {
  const targetMs = React.useMemo(() => new Date(target).getTime(), [target]);
  const [remaining, setRemaining] = useState<Remaining>(() => getRemaining(targetMs));

  useEffect(() => {
    setRemaining(getRemaining(targetMs));
    const id = setInterval(() => setRemaining(getRemaining(targetMs)), 1000);
    return () => clearInterval(id);
  }, [targetMs]);

  if (remaining.done) {
    return <div className="countdown-done">{doneLabel}</div>;
  }

  return (
    <div className="countdown" role="timer" aria-live="off">
      {UNITS.map(([key, label]) => (
        <div className="countdown-unit" key={key}>
          <span className="countdown-value">{String(remaining[key]).padStart(2, '0')}</span>
          <span className="countdown-label">{label}</span>
        </div>
      ))}
    </div>
  );
};

export default Countdown;
